import React, { useState, useEffect } from 'react';
import CommonModalLayout from '../Layouts/CommonModalLayout';
import {
    buttonStyles,
    addPostcardModalStyles,
} from '../../styles/commonStyles';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import MediaPreview from '../FileUpload/MediaPreview';
import ErrorMessage from '../Common/ErrorMessage';
import { useUpdateCatalogUpload } from '../../api/uploads';
import { getCollectionCategoriesGrouped } from '../../api/collections';
import { EnhancedCategoryCombobox } from '../ui/EnhancedCategoryCombobox';

const EditPostcardModal = ({ isOpen, onClose, upload }) => {
    const { t } = useTranslation();
    const [selectedCategory, setSelectedCategory] = useState('none');
    const [publish, setPublish] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const updateUpload = useUpdateCatalogUpload();

    const { data: groupedData } = useQuery(
        ['gallery-categories-all', 9],
        () => getCollectionCategoriesGrouped(9, { includeAll: true }),
        {
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
        }
    );

    const groupedCategories = groupedData?.groups || [];
    const categoriesWithoutGroup = groupedData?.categories_without_group || [];

    // Заполнить данные из upload при открытии
    useEffect(() => {
        if (isOpen && upload) {
            setSelectedCategory(upload.category_id ? String(upload.category_id) : 'none');
            setPublish(upload.is_approved !== false);
            setError('');
            setIsSaving(false);
        }
    }, [isOpen, upload]);

    const handleSave = async () => {
        setError('');
        setIsSaving(true);
        try {
            await updateUpload.mutateAsync({
                uploadId: upload.id,
                data: {
                    category_id: selectedCategory === 'none' ? null : parseInt(selectedCategory),
                    publish,
                },
            });
            onClose();
        } catch (err) {
            console.error('Error updating upload:', err);
            setError(err.message || 'Ошибка сохранения');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen || !upload) return null;

    const media = {
        cloud_url: upload.cloud_url || upload.image_url,
    };

    return (
        <CommonModalLayout
            isOpen={isOpen}
            onClose={onClose}
            title={t('editPostcardModal.title', 'Редактировать открытку')}
            closeOnOverlayClick={false}
            customStyles={addPostcardModalStyles}
        >
            <div className="px-0 py-0">
                <MediaPreview media={media} />

                {/* Категория */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('categories.selectCategory', 'Категория')}
                    </label>
                    <EnhancedCategoryCombobox
                        groupedCategories={groupedCategories}
                        categoriesWithoutGroup={categoriesWithoutGroup}
                        value={selectedCategory}
                        onChange={setSelectedCategory}
                        placeholder={t('categories.selectCategory', 'Категория')}
                        filterPlaceholder={t('categories.filterPlaceholder', 'Поиск...')}
                        showCreateOptions={false}
                    />
                </div>

                <ErrorMessage message={error} />

                {/* Публикация */}
                <label className="flex items-center gap-2 mb-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={publish}
                        onChange={(e) => setPublish(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                        Опубликовать в общий каталог
                    </span>
                </label>

                <div className="flex justify-end space-x-2 pt-2">
                    <button onClick={onClose} className={buttonStyles}>
                        {t('addPostcardModal.cancelButton', 'Отмена')}
                    </button>
                    <button
                        onClick={handleSave}
                        className={buttonStyles}
                        disabled={isSaving}
                    >
                        {isSaving
                            ? t('addPostcardModal.saving', 'Сохранение...')
                            : t('addPostcardModal.saveButton', 'Сохранить')
                        }
                    </button>
                </div>
            </div>
        </CommonModalLayout>
    );
};

export default EditPostcardModal;
